/**
 * NOVA-028 — RecallWorklistScreen. The biologiste lot-recall worklist (INV-4 → D-24). When a reagent lot
 * is recalled (quarantaine) or expires (perime), the already-released results produced on it may have
 * gone out wrong; this screen lists them — grouped by the recalled lot — so the biologiste re-decides
 * each via the D-24 correction flow (the WorklistScreen corrections tab). The list is DERIVED server-side
 * (nothing persisted), so a result drops out the moment it is corrected.
 *
 * Reaching the screen requires `validation.recall.read` (module-level + a local ForbiddenState guard).
 * It never mutates a result here — it routes the biologiste into the existing correction worklist.
 * The 5 states: loading/error/empty + offline + forbidden.
 */
import { Badge, Button, Card, EmptyState, ErrorState, ForbiddenState, LoadingState, OfflineState, Table, type Column } from "@/ui";
import { useT, useUiStore } from "@/app";
import { useCan } from "@/auth";
import { useNavigate } from "react-router-dom";
import { token } from "@/design/theme";
import { CAP, type RecallWorklistResultat } from "../types";
import { useRecallWorklist } from "../hooks/useValidation";
import { FlagBadge } from "../components/Badges";

export function RecallWorklistScreen() {
  const { t } = useT();
  const navigate = useNavigate();
  const online = useUiStore((s) => s.online);
  const canRead = useCan(CAP.recallRead);

  const { data, isLoading, isError, error, refetch } = useRecallWorklist();

  if (!online) return <OfflineState cached={false} />;
  if (!canRead) return <ForbiddenState title={t("validation.recall.title")} requiredCapabilities={[CAP.recallRead]} />;
  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState message={error?.message} onRetry={() => void refetch()} />;

  const groups = data?.groups ?? [];
  const total = data?.total_affected ?? 0;

  const columns: Column<RecallWorklistResultat>[] = [
    { key: "resultat", header: t("validation.recall.col.result"), mono: true, cell: (r) => <span className="nv-mono nv-ltr-island">#{String(r.resultat_id)}</span> },
    { key: "value", header: t("validation.recall.col.value"), mono: true, cell: (r) => <span className="nv-mono nv-ltr-island">{r.valeur ?? "—"}</span> },
    { key: "unit", header: t("validation.recall.col.unit"), cell: (r) => r.unite ?? "—" },
    { key: "flag", header: t("validation.recall.col.flag"), cell: (r) => <FlagBadge drapeau={r.drapeau} /> },
    { key: "status", header: t("validation.recall.col.status"), cell: (r) => <Badge tone="warn">{r.statut}</Badge> },
    {
      key: "action",
      header: "",
      align: "end",
      cell: () => (
        <Button size="sm" variant="secondary" onClick={() => navigate("/validation?tab=corrections")}>
          {t("validation.recall.correct")}
        </Button>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: token.s5 }}>
      <header style={{ display: "flex", flexDirection: "column", gap: token.s1 }}>
        <h1 style={{ fontSize: token.fsH1, margin: 0 }}>{t("validation.recall.title")}</h1>
        <span style={{ color: token.slate, fontSize: token.fsLabel }}>{t("validation.recall.subtitle")}</span>
      </header>

      {groups.length === 0 ? (
        <Card padded>
          <EmptyState glyph="⊘" title={t("validation.recall.empty.title")} description={t("validation.recall.empty.desc")} />
        </Card>
      ) : (
        <>
          <span style={{ color: token.steel, fontSize: token.fsLabel }}>{t("validation.recall.count", { n: total })}</span>
          {groups.map((g) => (
            <Card
              key={String(g.lot_id)}
              title={t("validation.recall.lot", { lot: g.numero_lot, reactif: g.reactif_libelle ?? g.reactif_code ?? "" })}
            >
              <div style={{ display: "flex", gap: token.s2, alignItems: "center", marginBlockEnd: token.s3, flexWrap: "wrap" }}>
                <Badge tone="critical">{t(`validation.recall.lotStatut.${g.lot_statut}`)}</Badge>
                <span className="nv-mono nv-ltr-island" style={{ color: token.steel }}>{g.reactif_code}</span>
                <span style={{ color: token.steel, fontSize: token.fsLabel }}>{t("validation.recall.affected", { n: g.affected_count })}</span>
              </div>
              <Table<RecallWorklistResultat>
                columns={columns}
                rows={g.resultats}
                rowKey={(r) => String(r.resultat_id)}
              />
            </Card>
          ))}
        </>
      )}
    </div>
  );
}
